import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jobsAPI } from "api";
import { Paging, Tab } from "api/types";
import { RootState } from "app/root-reducer";
import { RejectWithMessage } from "features/types";
import { getErrorMessage } from "utils/error";
import { JobPostNetworksModerationState, JobPostNetworksModerationTableResponse } from "./types";
import { PostNetworksModerationTabs, GetPostNetworksModerationTableFilters, PostNetworkSharingStatus } from 'features/post-networks-moderation/types';

export const getJobPostNetworksModerationTabs = createAsyncThunk<Tab[], void, RejectWithMessage>(
  'job-post-networks-moderation/get-tabs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.jobPostNetworksModeration.currentFilters;
      return await jobsAPI.getJobPostNetworksModerationTabs(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJobPostNetworksModerationTable = createAsyncThunk<JobPostNetworksModerationTableResponse, void, RejectWithMessage>(
  'job-post-networks-moderation/get-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.jobPostNetworksModeration.requestData, filters: state.jobPostNetworksModeration.currentFilters };
      return await jobsAPI.getJobSeekerNetworksModerationTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState: JobPostNetworksModerationState = {
  status: 'idle',
  currentFilters: { tab: PostNetworksModerationTabs.Pending },
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
  },
  data: {
    tabsIsLoading: false,
    tabs: [],
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  },
  error: null,
};

const jobPostNetworksModerationSlice = createSlice({
  name: 'jobPostNetworksModeration',
  initialState,
  reducers: {
    resetRequestData: (state) => {
      state.requestData = { ...initialState.requestData };
    },
    setFilters: (state, action: PayloadAction<Partial<GetPostNetworksModerationTableFilters>>) => {
      const newFilters = { ...state.currentFilters, ...action.payload };
      state.currentFilters = newFilters;
    },
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setTab: (state, action: PayloadAction<string>) => {
      state.currentFilters.tab = action.payload;
    },
    resetTableData: (state) => {
      state.data.tableData = initialState.data.tableData;
    },
    resetState: () => initialState,
    locallyUpdatePostStatus: (state, action: PayloadAction<{ postId: string; networkProgramId: string; status: PostNetworkSharingStatus }>) => {
      const { postId, networkProgramId } = action.payload;
      const rowIndex = state.data.tableData.rows.findIndex(r => r.id === postId);
      if (rowIndex === -1) return;

      const row = state.data.tableData.rows[rowIndex];
      const programs = row.networkProgramsJobCanBeSharedTo;
      const programIndex = programs.findIndex(np => np.id === networkProgramId);
      if (programIndex === -1) return;

      programs.splice(programIndex, 1);

      if (programs.length === 0) {
        state.data.tableData.rows.splice(rowIndex, 1);
        state.data.tableData.totalCount = Math.max(0, state.data.tableData.totalCount - 1);
        state.data.tableData.noResult = state.data.tableData.rows.length === 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJobPostNetworksModerationTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getJobPostNetworksModerationTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getJobPostNetworksModerationTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });

    builder.addCase(getJobPostNetworksModerationTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJobPostNetworksModerationTable.fulfilled, (state, action: PayloadAction<JobPostNetworksModerationTableResponse>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getJobPostNetworksModerationTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });
  },
});

export const { resetRequestData, setFilters, setPage, setTab, resetTableData, resetState, locallyUpdatePostStatus } = jobPostNetworksModerationSlice.actions;
export const jobPostNetworksModerationReducer = jobPostNetworksModerationSlice.reducer;
